import { CellContext, Row } from '@tanstack/react-table';
import { DateFormat } from 'application/Shared/Components/Core';
import { differenceInYears, startOfDay, subDays } from 'date-fns';
import React from 'react';

import { ColumnFilter } from '../Table/types';

/**
 * Mapping of date filter options to number of days.
 */
const activeExpiredDateMap: Record<string, number | undefined> = {
    active: 1,
    expired: 0,
    none: undefined
};

/**
 * Mapping of date filter options to number of days.
 */
const datesMap: Record<string, number> = {
    last7days: 7,
    last14days: 14,
    last30days: 30,
    last90days: 90
};

/**
 * Configuration object for date filtering control.
 */
export const dateFilter: ColumnFilter<unknown> = {
    control: 'Radio',
    defaultOptions: [{ options: Object.keys(datesMap) }]
};

/**
 * Configuration object for date filtering control.
 */
export const dateFilterEndDate: ColumnFilter<unknown> = {
    control: 'Radio',
    defaultOptions: [{ options: Object.keys(activeExpiredDateMap) }]
};

/**
 * Filters data based on whether a date is active, expired, or none.
 * - 'active': Date is in the future or equal to current date
 * - 'expired': Date is in the past
 * - 'none': Date value is null
 *
 * @template TData - The type of data being filtered
 * @param {Row<TData>} row - The row of data to check
 * @param {string} columnId - The ID of the column containing the date
 * @param {string[]} filterValue - The filter value (e.g., ['active'], ['expired'], ['none'])
 *
 * @throws {Error} If the filterValue is not a valid key in activeExpiredDateMap
 * @returns {boolean} - True if the date matches the filter criteria, false otherwise
 *
 * @example
 * // Returns true if the date in the row is in the future
 * activeExpiredDate(row, 'dateColumn', ['active'])
 *
 * // Returns true if the date in the row is in the past
 * activeExpiredDate(row, 'dateColumn', ['expired'])
 *
 * // Returns true if the date in the row is null
 * activeExpiredDate(row, 'dateColumn', ['none'])
 */
export function activeExpiredDate<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string[]
): boolean {
    if (!filterValue[0]) return true;

    if (!(filterValue[0] in activeExpiredDateMap)) {
        throw new Error(`Invalid filter value: ${filterValue}`);
    }

    if (filterValue[0] === 'none') {
        return !row.getValue(columnId);
    }

    if (!row.getValue(columnId)) return false;

    const rowDate = new Date(row.getValue(columnId) as string).getTime();
    const rightNow = new Date().getTime();

    if (filterValue[0] === 'active') {
        return rowDate >= rightNow;
    }

    if (filterValue[0] === 'expired') {
        return rowDate < rightNow;
    }

    return false;
}

/**
 * Filters data based on a date comparison within the last X days.
 * The comparison is done by normalizing both dates to the start of their respective days
 * to ensure consistent comparison regardless of the time component.
 *
 * @template TData - The type of data being filtered
 * @param {Row<TData>} row - The row of data to check
 * @param {string} columnId - The ID of the column containing the date
 * @param {string} filterValue - The filter value (e.g., ['last7days'], ['last30days'])
 *
 * @throws {Error} If the filterValue is not a valid key in datesMap
 * @returns {boolean} - True if the date is within the specified range, false otherwise
 *
 * @example
 * // Returns true if the date in the row is within the last 7 days
 * lastXDays(row, 'dateColumn', ['last7days'])
 *
 * // For midnight edge cases:
 * // If comparing '2024-01-15T00:00:00Z' with a 7-day filter on '2024-01-08T00:00:00Z',
 * // the function will return true as both dates are normalized to start of day
 */
export function lastXDays<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string[]
): boolean {
    if (!filterValue[0]) return true;

    if (!(filterValue[0] in datesMap)) {
        throw new Error(`Invalid filter value: ${filterValue}`);
    }

    const rowDate = new Date(row.getValue(columnId) as string);

    const comparisonDate = subDays(new Date(), datesMap[filterValue[0]]);

    // Set both dates to the start of their respective days
    const rowStartOfDay = startOfDay(rowDate);
    const comparisonStartOfDay = startOfDay(comparisonDate);

    return rowStartOfDay.getTime() >= comparisonStartOfDay.getTime();
}

/**
 * Mapping of date filter options to number of days.
 */
const datesOverMap: Record<string, number> = {
    never: 0,
    over30days: 30,
    over90days: 90,
    overAYear: 365
};

/**
 * Configuration object for date over X number of days filtering control.
 */
export const datesOverFilter: ColumnFilter<unknown> = {
    control: 'Radio',
    defaultOptions: [{ options: Object.keys(datesOverMap) }]
};

/**
 * Filters data based on a date comparison over X number of days.
 * The comparison is done by normalizing both dates to the start of their respective days
 * to ensure consistent comparison regardless of the time component.
 *
 * @template TData - The type of data being filtered
 * @param {Row<TData>} row - The row of data to check
 * @param {string} columnId - The ID of the column containing the date
 * @param {string} filterValue - The filter value (e.g., ['over30days'], ['overAYear'])
 *
 * @throws {Error} If the filterValue is not a valid key in datesMap
 * @returns {boolean} - True if the date is over the specified range, false otherwise
 *
 * @example
 * // Returns true if the date in the row is over the last 30 days
 * overXDays(row, 'dateColumn', ['over30days'])
 *
 * // For midnight edge cases:
 * // If comparing '2023-12-01T00:00:00Z' with an over 30-days filter on '2024-01-08T00:00:00Z',
 * // the function will return true as both dates are normalized to start of day
 */
export function overXDays<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string[]
): boolean {
    if (!filterValue[0]) return true;

    if (!(filterValue[0] in datesOverMap)) {
        throw new Error(`Invalid filter value: ${filterValue}`);
    }

    const rowDateValue = row.getValue(columnId);

    if (!rowDateValue) return filterValue[0] === 'never';

    const rowDate = new Date(rowDateValue as string);
    const comparisonDate = subDays(new Date(), datesOverMap[filterValue[0]]);

    // Set both dates to the start of their respective days
    const rowStartOfDay = startOfDay(rowDate);
    const comparisonStartOfDay = startOfDay(comparisonDate);

    if (filterValue[0] === 'overAYear') {
        return differenceInYears(startOfDay(new Date()), rowStartOfDay) >= 1;
    }
    if (filterValue[0] === 'over30days' || filterValue[0] === 'over90days') {
        return rowStartOfDay.getTime() < comparisonStartOfDay.getTime();
    }

    return false;
}

/**
 * Mapping of date filter options to number of days.
 */
const datesOver30Map: Record<string, number> = {
    over30days: 30
};

/**
 * Configuration object for date over X number of days filtering control.
 */
export const datesOver30Filter: ColumnFilter<unknown> = {
    control: 'Checkbox',
    defaultOptions: [{ options: Object.keys(datesOver30Map) }]
};

/**
 * Filters data based on a date comparison over 30 number of days.
 * The comparison is done by normalizing both dates to the start of their respective days
 * to ensure consistent comparison regardless of the time component.
 *
 * @template TData - The type of data being filtered
 * @param {Row<TData>} row - The row of data to check
 * @param {string} columnId - The ID of the column containing the date
 * @param {string} filterValue - The filter value (i.e., ['over30days'])
 *
 * @throws {Error} If the filterValue is not a valid key in datesMap
 * @returns {boolean} - True if the date is over 30 days, false otherwise
 *
 * @example
 * // Returns true if the date in the row is over the last 30 days
 * overXDays(row, 'dateColumn', ['over30days'])
 *
 * // For midnight edge cases:
 * // If comparing '2023-12-01T00:00:00Z' with an over 30-days filter on '2024-01-08T00:00:00Z',
 * // the function will return true as both dates are normalized to start of day
 */
export function over30days<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string[]
): boolean {
    if (!filterValue[0]) return true;

    if (!(filterValue[0] in datesOver30Map)) {
        throw new Error(`Invalid filter value: ${filterValue}`);
    }

    const rowDateValue = row.getValue(columnId);

    if (!rowDateValue) return false;

    const rowDate = new Date(rowDateValue as string);
    const comparisonDate = subDays(new Date(), datesOver30Map[filterValue[0]]);

    // Set both dates to the start of their respective days
    const rowStartOfDay = startOfDay(rowDate);
    const comparisonStartOfDay = startOfDay(comparisonDate);

    return rowStartOfDay.getTime() < comparisonStartOfDay.getTime();
}

/**
 * React component that renders a date in a formatted manner.
 * @template TData - The type of data being displayed
 * @param {CellContext<TData, unknown>} props - The cell context props
 * @param {CellContext<TData, unknown>} props.cell - The cell containing the date value
 * @param {number} [props.alertInactivity=0] - Number of days after which to show inactivity icon
 * @param {boolean} [props.noTz=true] - Whether to hide timezone information
 * @param {boolean} [props.noTimeString=false] - Whether to hide the timestring information
 * @param {string} [props.tooltipText] - Custom tooltip text to display on hover
 * @param {object} props.rest - Additional props passed to DateFormat component
 * @returns {JSX.Element} - Rendered DateFormat component
 */
export const DateFormatCell = <TData,>({
    cell,
    alertInactivity = 0,
    noTimeString = false,
    noTz = true,
    tooltipText,
    ...rest
}: CellContext<TData, string | number | Date | null> & {
    alertInactivity?: number;
    noTimeString?: boolean;
    noTz?: boolean;
    tooltipText?: string;
}): JSX.Element => (
    <DateFormat
        alertInactivity={alertInactivity}
        date={cell.getValue()}
        noTimeString={noTimeString}
        noTz={noTz}
        tooltipText={tooltipText}
        {...rest}
    />
);
